import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import {
    getPositions, getClients, getZones, getHeatmap,
    getHistoriqueGps, getCommerciaux, getZoneArborescence,
    getZoneDetail, getZoneStats
} from '../services/api';
import { useAuth } from '../contexts/AuthContext';
import L from 'leaflet';
import SearchableSelect from '../components/SearchableSelect';
import {
    FiMap, FiMapPin, FiLayers, FiUsers, FiNavigation,
    FiFilter, FiSearch, FiChevronRight, FiChevronDown,
    FiTarget, FiUser, FiX
} from 'react-icons/fi';

/* ─── Types ─── */
interface ZoneNode {
    id: number;
    nom: string;
    code?: string;
    type: string;
    responsable_nom?: string;
    latitude_centre?: number;
    longitude_centre?: number;
    rayon_km?: number;
    couleur?: string;
    description?: string;
    children?: ZoneNode[];
}

interface ZoneDetailData {
    id: number;
    nom: string;
    code?: string;
    type: string;
    responsable_nom?: string;
    description?: string;
    latitude_centre?: number;
    longitude_centre?: number;
    rayon_km?: number;
    couleur?: string;
    children?: { id: number; nom: string; type: string }[];
}

interface ZoneStatsData {
    nb_commerciaux: number;
    nb_clients: number;
    nb_activites: number;
    ca: number;
}

type LayerType = 'zones' | 'clients' | 'positions' | 'heatmap' | 'historique';

/* ─── Helpers ─── */
const SENEGAL_CENTER: [number, number] = [14.4974, -14.4524];

const ZONE_COLORS: Record<string, string> = {
    region: '#16A34A',
    departement: '#3B82F6',
    commune: '#8B5CF6',
    secteur: '#F59E0B',
};

const ZONE_TYPE_LABELS: Record<string, string> = {
    region: 'Region',
    departement: 'Departement',
    commune: 'Commune',
    secteur: 'Secteur',
};

const POTENTIEL_COLORS: Record<string, string> = {
    tres_eleve: '#3B82F6',
    eleve: '#16A34A',
    moyen: '#F59E0B',
    faible: '#94A3B8',
};

const formatCurrency = (v: number) =>
    new Intl.NumberFormat('fr-SN', { style: 'currency', currency: 'XOF', maximumFractionDigits: 0 }).format(v);

/* ─── Recursive tree filter ─── */
function filterTree(nodes: ZoneNode[], search: string, typeFilter: string, parentFilter: string): ZoneNode[] {
    const lower = search.toLowerCase();
    return nodes.reduce<ZoneNode[]>((acc, node) => {
        const matchesType = !typeFilter || node.type === typeFilter;
        const matchesSearch = !search || node.nom.toLowerCase().includes(lower) || (node.code || '').toLowerCase().includes(lower);
        const filteredChildren = node.children ? filterTree(node.children, search, typeFilter, parentFilter) : [];

        if (parentFilter && node.type === 'region' && node.id !== Number(parentFilter)) {
            // If filtering by parent region, skip regions that don't match (but check children)
            if (filteredChildren.length > 0) {
                acc.push({ ...node, children: filteredChildren });
            }
            return acc;
        }

        if ((matchesType && matchesSearch) || filteredChildren.length > 0) {
            acc.push({ ...node, children: filteredChildren });
        }
        return acc;
    }, []);
}

function flattenZones(nodes: ZoneNode[]): ZoneNode[] {
    const result: ZoneNode[] = [];
    const walk = (list: ZoneNode[]) => {
        list.forEach(n => {
            result.push(n);
            if (n.children) walk(n.children);
        });
    };
    walk(nodes);
    return result;
}

/* ─── ZoneTreeNode component ─── */
const ZoneTreeItem: React.FC<{
    node: ZoneNode;
    depth: number;
    selectedId: number | null;
    expandedIds: Set<number>;
    onToggle: (id: number) => void;
    onSelect: (node: ZoneNode) => void;
}> = ({ node, depth, selectedId, expandedIds, onToggle, onSelect }) => {
    const hasChildren = node.children && node.children.length > 0;
    const isExpanded = expandedIds.has(node.id);
    const isSelected = selectedId === node.id;
    const color = ZONE_COLORS[node.type] || '#94A3B8';

    return (
        <>
            <div
                className={`zone-tree-item ${isSelected ? 'zone-tree-item-active' : ''}`}
                style={{ paddingLeft: 12 + depth * 18 }}
                onClick={() => onSelect(node)}
            >
                <span
                    className="zone-tree-chevron"
                    onClick={e => { e.stopPropagation(); if (hasChildren) onToggle(node.id); }}
                    style={{ visibility: hasChildren ? 'visible' : 'hidden' }}
                >
                    {isExpanded ? <FiChevronDown size={14} /> : <FiChevronRight size={14} />}
                </span>
                <span
                    className="zone-tree-dot"
                    style={{ background: color }}
                />
                <div className="zone-tree-info">
                    <span className="zone-tree-name">{node.nom}</span>
                    <span className="zone-tree-meta">
                        <span className="badge" style={{ background: color, color: '#fff', fontSize: 10, padding: '1px 6px' }}>
                            {ZONE_TYPE_LABELS[node.type] || node.type}
                        </span>
                        {node.responsable_nom && (
                            <span style={{ fontSize: 11, color: 'var(--text-muted)' }}>
                                <FiUser size={10} /> {node.responsable_nom}
                            </span>
                        )}
                    </span>
                </div>
            </div>
            {hasChildren && isExpanded && node.children!.map(child => (
                <ZoneTreeItem
                    key={child.id}
                    node={child}
                    depth={depth + 1}
                    selectedId={selectedId}
                    expandedIds={expandedIds}
                    onToggle={onToggle}
                    onSelect={onSelect}
                />
            ))}
        </>
    );
};

/* SearchableSelect imported from ../components/SearchableSelect */

/* ═══════════════════════════════════════════════
   MAIN COMPONENT
   ═══════════════════════════════════════════════ */
const Cartographie: React.FC = () => {
    const { isManager, user } = useAuth();
    const mapRef = useRef<L.Map | null>(null);
    const mapContainerRef = useRef<HTMLDivElement>(null);
    const markersRef = useRef<L.LayerGroup | null>(null);
    const clientsLayerRef = useRef<L.LayerGroup | null>(null);

    /* ─── State ─── */
    const [sidebarOpen, setSidebarOpen] = useState(true);
    const [layer, setLayer] = useState<LayerType>('zones');
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);

    // Zone tree
    const [arborescence, setArborescence] = useState<ZoneNode[]>([]);
    const [expandedIds, setExpandedIds] = useState<Set<number>>(new Set());
    const [selectedZoneId, setSelectedZoneId] = useState<number | null>(null);
    const [treeSearch, setTreeSearch] = useState('');

    // Zone detail panel
    const [zoneDetail, setZoneDetail] = useState<ZoneDetailData | null>(null);
    const [zoneStats, setZoneStats] = useState<ZoneStatsData | null>(null);
    const [detailLoading, setDetailLoading] = useState(false);
    const [showDetail, setShowDetail] = useState(false);

    // Clients always-on
    const [showClients, setShowClients] = useState(true);

    // Filters
    const [zoneTypeFilter, setZoneTypeFilter] = useState('');
    const [parentFilter, setParentFilter] = useState('');
    const [commerciaux, setCommerciaux] = useState<any[]>([]);
    const [selectedUser, setSelectedUser] = useState('');
    const [potentielFilter, setPotentielFilter] = useState('');
    const [dateDebut, setDateDebut] = useState(new Date().toISOString().split('T')[0]);
    const [dateFin, setDateFin] = useState(new Date().toISOString().split('T')[0]);
    const [historiqueDate, setHistoriqueDate] = useState(new Date().toISOString().split('T')[0]);
    const [historiqueUser, setHistoriqueUser] = useState('');

    /* ─── Derived data ─── */
    const regions = useMemo(() =>
        arborescence.filter(n => n.type === 'region'),
        [arborescence]
    );

    const filteredTree = useMemo(() =>
        filterTree(arborescence, treeSearch, zoneTypeFilter, parentFilter),
        [arborescence, treeSearch, zoneTypeFilter, parentFilter]
    );

    /* ─── Init map ─── */
    useEffect(() => {
        if (!mapContainerRef.current || mapRef.current) return;

        mapRef.current = L.map(mapContainerRef.current).setView(SENEGAL_CENTER, 7);
        L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
            attribution: '&copy; OpenStreetMap'
        }).addTo(mapRef.current);

        markersRef.current = L.layerGroup().addTo(mapRef.current);
        clientsLayerRef.current = L.layerGroup().addTo(mapRef.current);

        return () => {
            mapRef.current?.remove();
            mapRef.current = null;
        };
    }, []);

    /* ─── Invalidate map size when sidebar toggles ─── */
    useEffect(() => {
        setTimeout(() => { mapRef.current?.invalidateSize(); }, 320);
    }, [sidebarOpen]);

    /* ─── Load initial data ─── */
    useEffect(() => {
        loadArborescence();
        if (isManager()) {
            getCommerciaux().then(r => setCommerciaux(r.data)).catch(() => {});
        }
    }, []);

    /* ─── Always-on clients layer ─── */
    useEffect(() => {
        loadClientsOverlay();
    }, [showClients, potentielFilter, selectedZoneId]);

    const loadClientsOverlay = async () => {
        if (!clientsLayerRef.current) return;
        clientsLayerRef.current.clearLayers();
        if (!showClients) return;

        try {
            const params: any = { limit: 1000 };
            if (potentielFilter) params.potentiel = potentielFilter;
            if (selectedZoneId) params.zone_id = selectedZoneId;

            const res = await getClients(params);
            const clients = res.data.data || res.data;

            clients
                .filter((c: any) => c.latitude && c.longitude)
                .forEach((c: any) => {
                    const color = POTENTIEL_COLORS[c.potentiel] || '#94A3B8';
                    const icon = L.divIcon({
                        className: 'client-map-icon',
                        html: `<div style="
                            position:relative;width:28px;height:34px;
                        ">
                            <svg viewBox="0 0 28 34" width="28" height="34" xmlns="http://www.w3.org/2000/svg">
                                <path d="M14 0C6.27 0 0 6.27 0 14c0 10.5 14 20 14 20s14-9.5 14-20C28 6.27 21.73 0 14 0z" fill="${color}" stroke="#fff" stroke-width="1.5"/>
                                <circle cx="14" cy="13" r="8" fill="#fff" opacity="0.9"/>
                                <path d="M10 10h8v1h-8zM9 12h10v1H9zM10 14h8v2a1 1 0 01-1 1h-6a1 1 0 01-1-1v-2z" fill="${color}" opacity="0.85"/>
                            </svg>
                        </div>`,
                        iconSize: [28, 34],
                        iconAnchor: [14, 34],
                        popupAnchor: [0, -34],
                    });
                    const marker = L.marker([c.latitude, c.longitude], { icon }).bindPopup(`
                        <div style="min-width:160px">
                            <strong>${c.raison_sociale}</strong><br/>
                            ${c.ville || ''}<br/>
                            <span style="color:${color};font-weight:600">Potentiel: ${c.potentiel}</span><br/>
                            Commercial: ${c.commercial_nom || '-'}
                        </div>
                    `);
                    clientsLayerRef.current!.addLayer(marker);
                });
        } catch {}
    };

    /* ─── Reload layer when deps change ─── */
    useEffect(() => {
        loadLayer();
    }, [layer, selectedUser, potentielFilter, dateDebut, dateFin, historiqueDate, historiqueUser, selectedZoneId]);

    /* ─── Data loaders ─── */
    const loadArborescence = async () => {
        try {
            const res = await getZoneArborescence();
            setArborescence(res.data);
            // Expand top-level by default
            const topIds = new Set<number>(res.data.map((z: ZoneNode) => z.id));
            setExpandedIds(topIds);
        } catch {
            // Fallback: load flat zones and build simple tree
            try {
                const res = await getZones();
                setArborescence(res.data);
            } catch {
                setError('Impossible de charger les zones');
            }
        }
    };

    const clearMarkers = () => { markersRef.current?.clearLayers(); };

    const loadLayer = async () => {
        clearMarkers();
        if (!mapRef.current || !markersRef.current) return;
        setLoading(true);
        setError(null);

        try {
            switch (layer) {
                case 'zones':
                    await loadZonesLayer();
                    break;
                case 'clients':
                    // Clients are now shown via always-on overlay
                    break;
                case 'positions':
                    if (isManager()) await loadPositionsLayer();
                    break;
                case 'heatmap':
                    if (isManager()) await loadHeatmapLayer();
                    break;
                case 'historique':
                    if (isManager()) await loadHistoriqueLayer();
                    break;
            }
        } catch (err: any) {
            setError(err?.response?.data?.message || 'Erreur de chargement des donnees');
        } finally {
            setLoading(false);
        }
    };

    const loadZonesLayer = async () => {
        // If a zone is selected, show that zone and its children
        const allZones = flattenZones(arborescence);
        let zonesToDraw = allZones;

        if (selectedZoneId) {
            const findNode = (nodes: ZoneNode[], id: number): ZoneNode | null => {
                for (const n of nodes) {
                    if (n.id === id) return n;
                    if (n.children) {
                        const found = findNode(n.children, id);
                        if (found) return found;
                    }
                }
                return null;
            };
            const sel = findNode(arborescence, selectedZoneId);
            if (sel) {
                zonesToDraw = [sel, ...flattenZones(sel.children || [])];
            }
        }

        // Also fetch from API for full data
        const res = await getZones();
        const apiZones: any[] = res.data;
        const treeIds = new Set(zonesToDraw.map(z => z.id));

        apiZones
            .filter((z: any) => !selectedZoneId || treeIds.has(z.id))
            .forEach((z: any) => {
                if (z.latitude_centre && z.longitude_centre) {
                    const typeColor = ZONE_COLORS[z.type] || z.couleur || '#94A3B8';
                    const isSelected = z.id === selectedZoneId;

                    if (z.rayon_km) {
                        const circle = L.circle(
                            [z.latitude_centre, z.longitude_centre],
                            {
                                radius: z.rayon_km * 1000,
                                color: typeColor,
                                fillColor: typeColor,
                                fillOpacity: isSelected ? 0.3 : 0.12,
                                weight: isSelected ? 3 : 1.5,
                                dashArray: isSelected ? '' : '4 4',
                            }
                        ).bindPopup(`
                            <div style="min-width:160px">
                                <strong>${z.nom}</strong><br/>
                                <span style="color:${typeColor};font-size:11px;font-weight:600">${ZONE_TYPE_LABELS[z.type] || z.type}</span><br/>
                                ${z.responsable_nom ? '<em>' + z.responsable_nom + '</em><br/>' : ''}
                                ${z.nb_commerciaux != null ? z.nb_commerciaux + ' commerciaux<br/>' : ''}
                                ${z.nb_clients != null ? z.nb_clients + ' clients' : ''}
                            </div>
                        `);
                        markersRef.current!.addLayer(circle);
                    }

                    // Label
                    const label = L.marker([z.latitude_centre, z.longitude_centre], {
                        icon: L.divIcon({
                            className: 'zone-label-icon',
                            html: `<span style="
                                font-size:${z.type === 'region' ? 13 : 11}px;
                                font-weight:${z.type === 'region' ? 700 : 500};
                                color:${typeColor};
                                text-shadow: 1px 1px 2px #fff, -1px -1px 2px #fff;
                                white-space:nowrap;
                            ">${z.nom}</span>`,
                            iconSize: [0, 0],
                            iconAnchor: [0, -12],
                        })
                    });
                    markersRef.current!.addLayer(label);
                }
            });
    };

    const loadClientsLayer = async () => {
        const params: any = { limit: 1000 };
        if (potentielFilter) params.potentiel = potentielFilter;
        if (selectedZoneId) params.zone_id = selectedZoneId;

        const res = await getClients(params);
        const clients = res.data.data || res.data;

        clients
            .filter((c: any) => c.latitude && c.longitude)
            .forEach((c: any) => {
                const color = POTENTIEL_COLORS[c.potentiel] || '#94A3B8';
                const marker = L.circleMarker([c.latitude, c.longitude], {
                    radius: 7,
                    fillColor: color,
                    color: '#fff',
                    weight: 2,
                    fillOpacity: 0.85,
                }).bindPopup(`
                    <div style="min-width:160px">
                        <strong>${c.raison_sociale}</strong><br/>
                        ${c.ville || ''}<br/>
                        <span style="color:${color};font-weight:600">Potentiel: ${c.potentiel}</span><br/>
                        Commercial: ${c.commercial_nom || '-'}
                    </div>
                `);
                markersRef.current!.addLayer(marker);
            });
    };

    const loadPositionsLayer = async () => {
        const res = await getPositions();
        const bounds: L.LatLng[] = [];
        res.data.forEach((p: any) => {
            const latlng = L.latLng(p.latitude, p.longitude);
            bounds.push(latlng);
            const marker = L.marker(latlng).bindPopup(`
                <div style="min-width:140px">
                    <strong>${p.nom}</strong><br/>
                    Derniere position:<br/>
                    ${new Date(p.horodatage).toLocaleString('fr-FR')}
                </div>
            `);
            markersRef.current!.addLayer(marker);
        });
        if (bounds.length > 0 && mapRef.current) {
            mapRef.current.fitBounds(L.latLngBounds(bounds), { padding: [40, 40], maxZoom: 13 });
        }
    };

    const loadHeatmapLayer = async () => {
        const params: any = {};
        if (dateDebut) params.date_debut = dateDebut;
        if (dateFin) params.date_fin = dateFin;

        const res = await getHeatmap(params);
        res.data.forEach((p: any) => {
            const circle = L.circleMarker([p.latitude, p.longitude], {
                radius: Math.min(20, 3 + (p.intensite || 1)),
                fillColor: '#EF4444',
                color: 'transparent',
                fillOpacity: 0.4,
            });
            markersRef.current!.addLayer(circle);
        });
    };

    const loadHistoriqueLayer = async () => {
        const userId = historiqueUser || selectedUser;
        if (!userId) return;

        const res = await getHistoriqueGps(Number(userId), { date: historiqueDate });
        if (res.data.length > 0) {
            const latlngs = res.data.map((t: any) => [t.latitude, t.longitude]);
            const polyline = L.polyline(latlngs as L.LatLngExpression[], {
                color: '#3B82F6',
                weight: 3,
                opacity: 0.8,
            });
            markersRef.current!.addLayer(polyline);

            // Start marker
            const startIcon = L.divIcon({
                className: 'custom-div-icon',
                html: '<div style="background:#16A34A;color:#fff;border-radius:50%;width:24px;height:24px;display:flex;align-items:center;justify-content:center;font-size:11px;font-weight:700;border:2px solid #fff;box-shadow:0 2px 6px rgba(0,0,0,.3)">D</div>',
                iconSize: [24, 24],
                iconAnchor: [12, 12],
            });
            const endIcon = L.divIcon({
                className: 'custom-div-icon',
                html: '<div style="background:#EF4444;color:#fff;border-radius:50%;width:24px;height:24px;display:flex;align-items:center;justify-content:center;font-size:11px;font-weight:700;border:2px solid #fff;box-shadow:0 2px 6px rgba(0,0,0,.3)">A</div>',
                iconSize: [24, 24],
                iconAnchor: [12, 12],
            });

            L.marker(latlngs[0], { icon: startIcon }).bindPopup('Depart').addTo(markersRef.current!);
            L.marker(latlngs[latlngs.length - 1], { icon: endIcon }).bindPopup('Arrivee').addTo(markersRef.current!);

            mapRef.current!.fitBounds(polyline.getBounds(), { padding: [40, 40] });
        }
    };

    /* ─── Zone selection ─── */
    const handleSelectZone = useCallback(async (node: ZoneNode) => {
        setSelectedZoneId(node.id);
        setShowDetail(true);
        setDetailLoading(true);

        // Fly to zone
        if (node.latitude_centre && node.longitude_centre && mapRef.current) {
            const zoom = node.type === 'region' ? 8 : node.type === 'departement' ? 10 : 12;
            mapRef.current.flyTo([node.latitude_centre, node.longitude_centre], zoom, { duration: 1.2 });
        }

        // Load detail + stats
        try {
            const [detailRes, statsRes] = await Promise.allSettled([
                getZoneDetail(node.id),
                getZoneStats(node.id),
            ]);

            if (detailRes.status === 'fulfilled') {
                setZoneDetail(detailRes.value.data);
            } else {
                // Fallback to node data
                setZoneDetail({
                    id: node.id,
                    nom: node.nom,
                    code: node.code,
                    type: node.type,
                    responsable_nom: node.responsable_nom,
                    latitude_centre: node.latitude_centre,
                    longitude_centre: node.longitude_centre,
                    rayon_km: node.rayon_km,
                    children: node.children?.map(c => ({ id: c.id, nom: c.nom, type: c.type })),
                });
            }

            if (statsRes.status === 'fulfilled') {
                setZoneStats(statsRes.value.data);
            } else {
                setZoneStats(null);
            }
        } catch {
            setZoneDetail(null);
            setZoneStats(null);
        } finally {
            setDetailLoading(false);
        }
    }, [arborescence]);

    const handleToggleExpand = useCallback((id: number) => {
        setExpandedIds(prev => {
            const next = new Set(prev);
            if (next.has(id)) next.delete(id);
            else next.add(id);
            return next;
        });
    }, []);

    const handleClearSelection = () => {
        setSelectedZoneId(null);
        setShowDetail(false);
        setZoneDetail(null);
        setZoneStats(null);
        if (mapRef.current) {
            mapRef.current.flyTo(SENEGAL_CENTER, 7, { duration: 1 });
        }
    };

    const handleZoomToZone = () => {
        if (zoneDetail?.latitude_centre && zoneDetail?.longitude_centre && mapRef.current) {
            const zoom = zoneDetail.type === 'region' ? 8 : zoneDetail.type === 'departement' ? 10 : 12;
            mapRef.current.flyTo([zoneDetail.latitude_centre, zoneDetail.longitude_centre], zoom, { duration: 1.2 });
        }
    };

    /* ─── Render ─── */
    return (
        <div className="page-cartographie" style={{ position: 'relative' }}>
            {/* Page header */}
            <div className="page-header">
                <h2><FiMap /> Cartographie</h2>
                <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                    <button
                        className="btn btn-sm btn-secondary"
                        onClick={() => setSidebarOpen(p => !p)}
                        title={sidebarOpen ? 'Masquer le panneau' : 'Afficher le panneau'}
                    >
                        <FiLayers /> {sidebarOpen ? 'Masquer zones' : 'Afficher zones'}
                    </button>
                    {selectedZoneId && (
                        <button className="btn btn-sm btn-secondary" onClick={handleClearSelection}>
                            <FiTarget /> Vue globale
                        </button>
                    )}
                </div>
            </div>

            {/* Filters bar */}
            <div className="filters-bar" style={{ flexWrap: 'wrap' }}>
                {/* Layer buttons */}
                <div className="layer-buttons">
                    <button className={`btn btn-sm ${layer === 'zones' ? 'btn-primary' : 'btn-secondary'}`} onClick={() => setLayer('zones')}>
                        <FiLayers /> Zones
                    </button>
                    <button
                        className={`btn btn-sm ${showClients ? 'btn-primary' : 'btn-secondary'}`}
                        onClick={() => setShowClients(p => !p)}
                        title="Afficher/masquer les clients"
                    >
                        <FiMapPin /> Clients {showClients ? '✓' : ''}
                    </button>
                    {isManager() && (
                        <button className={`btn btn-sm ${layer === 'positions' ? 'btn-primary' : 'btn-secondary'}`} onClick={() => setLayer('positions')}>
                            <FiUsers /> Positions
                        </button>
                    )}
                    {isManager() && (
                        <button className={`btn btn-sm ${layer === 'heatmap' ? 'btn-primary' : 'btn-secondary'}`} onClick={() => setLayer('heatmap')}>
                            <FiTarget /> Heatmap
                        </button>
                    )}
                    {isManager() && (
                        <button className={`btn btn-sm ${layer === 'historique' ? 'btn-primary' : 'btn-secondary'}`} onClick={() => setLayer('historique')}>
                            <FiNavigation /> Historique
                        </button>
                    )}
                </div>

                <div style={{ width: 1, height: 24, background: 'var(--border)' }} />

                {/* Contextual filters */}
                {layer === 'zones' && (
                    <>
                        <select
                            value={zoneTypeFilter}
                            onChange={e => setZoneTypeFilter(e.target.value)}
                            title="Type de zone"
                        >
                            <option value="">Toutes les zones</option>
                            <option value="region">Region</option>
                            <option value="departement">Departement</option>
                            <option value="commune">Commune</option>
                            <option value="secteur">Secteur</option>
                        </select>
                        <select
                            value={parentFilter}
                            onChange={e => setParentFilter(e.target.value)}
                            title="Region parente"
                        >
                            <option value="">Toutes les regions</option>
                            {regions.map(r => (
                                <option key={r.id} value={r.id}>{r.nom}</option>
                            ))}
                        </select>
                    </>
                )}

                {showClients && (
                    <select
                        value={potentielFilter}
                        onChange={e => setPotentielFilter(e.target.value)}
                        title="Potentiel client"
                    >
                        <option value="">Tous potentiels</option>
                        <option value="tres_eleve">Tres eleve</option>
                        <option value="eleve">Eleve</option>
                        <option value="moyen">Moyen</option>
                        <option value="faible">Faible</option>
                    </select>
                )}

                {layer === 'positions' && isManager() && (
                    <SearchableSelect
                        value={selectedUser}
                        onChange={setSelectedUser}
                        placeholder="Tous les commerciaux"
                        options={commerciaux.map((c: any) => ({ value: String(c.id), label: `${c.prenom} ${c.nom}` }))}
                    />
                )}

                {layer === 'heatmap' && (
                    <>
                        <label style={{ fontSize: 12, color: 'var(--text-muted)', margin: 0 }}>Du</label>
                        <input
                            type="date"
                            value={dateDebut}
                            onChange={e => setDateDebut(e.target.value)}
                        />
                        <label style={{ fontSize: 12, color: 'var(--text-muted)', margin: 0 }}>au</label>
                        <input
                            type="date"
                            value={dateFin}
                            onChange={e => setDateFin(e.target.value)}
                        />
                    </>
                )}

                {layer === 'historique' && (
                    <>
                        <SearchableSelect
                            value={historiqueUser}
                            onChange={setHistoriqueUser}
                            placeholder="-- Commercial --"
                            options={commerciaux.map((c: any) => ({ value: String(c.id), label: `${c.prenom} ${c.nom}` }))}
                        />
                        <input
                            type="date"
                            value={historiqueDate}
                            onChange={e => setHistoriqueDate(e.target.value)}
                        />
                    </>
                )}

                {loading && (
                    <span style={{ fontSize: 12, color: 'var(--text-muted)', display: 'flex', alignItems: 'center', gap: 6 }}>
                        <span className="spinner" style={{
                            width: 14, height: 14, border: '2px solid var(--border)',
                            borderTopColor: 'var(--primary)', borderRadius: '50%',
                            animation: 'spin 0.6s linear infinite', display: 'inline-block'
                        }} />
                        Chargement...
                    </span>
                )}
            </div>

            {/* Error banner */}
            {error && (
                <div style={{
                    background: 'var(--red-light)', color: 'var(--danger)',
                    padding: '8px 14px', borderRadius: 'var(--r-xs)',
                    fontSize: 13, marginBottom: 12, fontWeight: 500,
                }}>
                    {error}
                </div>
            )}

            {/* Main layout: sidebar + map + detail panel */}
            <div style={{
                display: 'flex',
                gap: 0,
                height: 'calc(100vh - 230px)',
                minHeight: 400,
                borderRadius: 'var(--r-md)',
                overflow: 'hidden',
                border: '1px solid var(--border)',
                background: '#fff',
            }}>

                {/* LEFT SIDEBAR - Zone tree */}
                {sidebarOpen && (
                    <div style={{
                        width: 320,
                        minWidth: 320,
                        borderRight: '1px solid var(--border)',
                        display: 'flex',
                        flexDirection: 'column',
                        background: '#fff',
                        overflow: 'hidden',
                    }}>
                        {/* Search */}
                        <div style={{
                            padding: '12px 12px 8px',
                            borderBottom: '1px solid var(--divider)',
                        }}>
                            <div style={{
                                display: 'flex', alignItems: 'center', gap: 8,
                                background: 'var(--bg)', borderRadius: 'var(--r-xs)',
                                padding: '6px 10px', border: '1px solid var(--border)',
                            }}>
                                <FiSearch size={14} color="var(--text-muted)" />
                                <input
                                    type="text"
                                    value={treeSearch}
                                    onChange={e => setTreeSearch(e.target.value)}
                                    placeholder="Rechercher une zone..."
                                    style={{
                                        border: 'none', background: 'transparent', outline: 'none',
                                        fontSize: 13, flex: 1, color: 'var(--text)',
                                        fontFamily: 'inherit',
                                    }}
                                />
                            </div>
                        </div>

                        {/* Tree */}
                        <div style={{
                            flex: 1,
                            overflowY: 'auto',
                            padding: '4px 0',
                        }}>
                            {filteredTree.length === 0 ? (
                                <div style={{
                                    padding: 24, textAlign: 'center',
                                    color: 'var(--text-muted)', fontSize: 13,
                                }}>
                                    Aucune zone trouvee
                                </div>
                            ) : (
                                filteredTree.map(node => (
                                    <ZoneTreeItem
                                        key={node.id}
                                        node={node}
                                        depth={0}
                                        selectedId={selectedZoneId}
                                        expandedIds={expandedIds}
                                        onToggle={handleToggleExpand}
                                        onSelect={handleSelectZone}
                                    />
                                ))
                            )}
                        </div>

                        {/* Summary footer */}
                        <div style={{
                            padding: '8px 12px',
                            borderTop: '1px solid var(--divider)',
                            fontSize: 11,
                            color: 'var(--text-muted)',
                        }}>
                            {flattenZones(arborescence).length} zones au total
                        </div>
                    </div>
                )}

                {/* MAP */}
                <div style={{ flex: 1, position: 'relative', minWidth: 0 }}>
                    <div
                        ref={mapContainerRef}
                        style={{ width: '100%', height: '100%' }}
                    />

                    {/* Legend overlay */}
                    <div style={{
                        position: 'absolute', bottom: 12, left: 12, zIndex: 1000,
                        background: 'rgba(255,255,255,0.95)', borderRadius: 'var(--r-xs)',
                        padding: '8px 12px', fontSize: 11, boxShadow: 'var(--sh-sm)',
                        backdropFilter: 'blur(4px)',
                    }}>
                        {layer === 'zones' && (
                            <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap' }}>
                                {Object.entries(ZONE_TYPE_LABELS).map(([key, label]) => (
                                    <span key={key} style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                                        <span style={{
                                            width: 10, height: 10, borderRadius: '50%',
                                            background: ZONE_COLORS[key], display: 'inline-block',
                                        }} />
                                        {label}
                                    </span>
                                ))}
                            </div>
                        )}
                        {layer === 'clients' && (
                            <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap' }}>
                                {Object.entries(POTENTIEL_COLORS).map(([key, color]) => (
                                    <span key={key} style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                                        <span style={{
                                            width: 10, height: 10, borderRadius: '50%',
                                            background: color, display: 'inline-block',
                                        }} />
                                        {key.replace('_', ' ')}
                                    </span>
                                ))}
                            </div>
                        )}
                        {layer === 'historique' && (
                            <div style={{ display: 'flex', gap: 12, alignItems: 'center' }}>
                                <span style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                                    <span style={{ width: 10, height: 10, borderRadius: '50%', background: '#16A34A', display: 'inline-block' }} />
                                    Depart
                                </span>
                                <span style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                                    <span style={{ width: 10, height: 10, borderRadius: '50%', background: '#EF4444', display: 'inline-block' }} />
                                    Arrivee
                                </span>
                                <span style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                                    <span style={{ width: 20, height: 3, background: '#3B82F6', display: 'inline-block', borderRadius: 2 }} />
                                    Trajet
                                </span>
                            </div>
                        )}
                    </div>
                </div>

                {/* RIGHT PANEL - Zone detail */}
                {showDetail && (
                    <div style={{
                        width: 300,
                        minWidth: 300,
                        borderLeft: '1px solid var(--border)',
                        display: 'flex',
                        flexDirection: 'column',
                        background: '#fff',
                        overflow: 'hidden',
                    }}>
                        {/* Detail header */}
                        <div style={{
                            padding: '12px 14px',
                            borderBottom: '1px solid var(--divider)',
                            display: 'flex',
                            justifyContent: 'space-between',
                            alignItems: 'center',
                        }}>
                            <span style={{ fontWeight: 700, fontSize: 14 }}>Detail de la zone</span>
                            <button
                                className="btn-icon"
                                onClick={() => { setShowDetail(false); setSelectedZoneId(null); }}
                                style={{ fontSize: 18, lineHeight: 1 }}
                                title="Fermer"
                            >
                                &times;
                            </button>
                        </div>

                        {/* Detail body */}
                        <div style={{ flex: 1, overflowY: 'auto', padding: 14 }}>
                            {detailLoading ? (
                                <div style={{ textAlign: 'center', padding: 32, color: 'var(--text-muted)' }}>
                                    <div style={{
                                        width: 28, height: 28, border: '3px solid var(--border)',
                                        borderTopColor: 'var(--primary)', borderRadius: '50%',
                                        animation: 'spin 0.6s linear infinite',
                                        margin: '0 auto 12px',
                                    }} />
                                    Chargement...
                                </div>
                            ) : zoneDetail ? (
                                <>
                                    {/* Zone info */}
                                    <div style={{ marginBottom: 16 }}>
                                        <h3 style={{ fontSize: 16, fontWeight: 700, marginBottom: 6 }}>
                                            {zoneDetail.nom}
                                        </h3>
                                        <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginBottom: 8 }}>
                                            <span className="badge" style={{
                                                background: ZONE_COLORS[zoneDetail.type] || '#94A3B8',
                                                color: '#fff', fontSize: 11,
                                            }}>
                                                {ZONE_TYPE_LABELS[zoneDetail.type] || zoneDetail.type}
                                            </span>
                                            {zoneDetail.code && (
                                                <span className="badge" style={{ fontSize: 11 }}>
                                                    {zoneDetail.code}
                                                </span>
                                            )}
                                        </div>
                                        {zoneDetail.responsable_nom && (
                                            <div style={{
                                                fontSize: 13, color: 'var(--text-secondary)',
                                                display: 'flex', alignItems: 'center', gap: 6, marginBottom: 4,
                                            }}>
                                                <FiUser size={13} />
                                                Responsable: <strong>{zoneDetail.responsable_nom}</strong>
                                            </div>
                                        )}
                                        {zoneDetail.description && (
                                            <p style={{ fontSize: 12, color: 'var(--text-muted)', marginTop: 6, lineHeight: 1.5 }}>
                                                {zoneDetail.description}
                                            </p>
                                        )}
                                    </div>

                                    {/* Stats */}
                                    {zoneStats && (
                                        <div style={{ marginBottom: 16 }}>
                                            <h4 style={{ fontSize: 12, fontWeight: 700, textTransform: 'uppercase', color: 'var(--text-muted)', marginBottom: 10, letterSpacing: 0.5 }}>
                                                Statistiques
                                            </h4>
                                            <div style={{
                                                display: 'grid',
                                                gridTemplateColumns: '1fr 1fr',
                                                gap: 8,
                                            }}>
                                                <div style={{
                                                    background: 'var(--primary-light)', borderRadius: 'var(--r-xs)',
                                                    padding: '10px 12px', textAlign: 'center',
                                                }}>
                                                    <div style={{ fontSize: 18, fontWeight: 800, color: 'var(--primary-dark)' }}>
                                                        {zoneStats.nb_commerciaux}
                                                    </div>
                                                    <div style={{ fontSize: 10, color: 'var(--primary-dark)', fontWeight: 600 }}>
                                                        Commerciaux
                                                    </div>
                                                </div>
                                                <div style={{
                                                    background: 'var(--blue-light)', borderRadius: 'var(--r-xs)',
                                                    padding: '10px 12px', textAlign: 'center',
                                                }}>
                                                    <div style={{ fontSize: 18, fontWeight: 800, color: 'var(--blue)' }}>
                                                        {zoneStats.nb_clients}
                                                    </div>
                                                    <div style={{ fontSize: 10, color: 'var(--blue)', fontWeight: 600 }}>
                                                        Clients
                                                    </div>
                                                </div>
                                                <div style={{
                                                    background: 'var(--orange-light)', borderRadius: 'var(--r-xs)',
                                                    padding: '10px 12px', textAlign: 'center',
                                                }}>
                                                    <div style={{ fontSize: 18, fontWeight: 800, color: '#B45309' }}>
                                                        {zoneStats.nb_activites}
                                                    </div>
                                                    <div style={{ fontSize: 10, color: '#B45309', fontWeight: 600 }}>
                                                        Activites
                                                    </div>
                                                </div>
                                                <div style={{
                                                    background: 'var(--purple-light)', borderRadius: 'var(--r-xs)',
                                                    padding: '10px 12px', textAlign: 'center',
                                                }}>
                                                    <div style={{ fontSize: 14, fontWeight: 800, color: 'var(--purple)' }}>
                                                        {formatCurrency(zoneStats.ca || 0)}
                                                    </div>
                                                    <div style={{ fontSize: 10, color: 'var(--purple)', fontWeight: 600 }}>
                                                        CA
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    )}

                                    {/* Children list */}
                                    {zoneDetail.children && zoneDetail.children.length > 0 && (
                                        <div style={{ marginBottom: 16 }}>
                                            <h4 style={{ fontSize: 12, fontWeight: 700, textTransform: 'uppercase', color: 'var(--text-muted)', marginBottom: 10, letterSpacing: 0.5 }}>
                                                Sous-zones ({zoneDetail.children.length})
                                            </h4>
                                            <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                                                {zoneDetail.children.map((child) => (
                                                    <div
                                                        key={child.id}
                                                        style={{
                                                            display: 'flex', alignItems: 'center', gap: 8,
                                                            padding: '6px 10px', borderRadius: 'var(--r-xs)',
                                                            background: 'var(--bg)', cursor: 'pointer',
                                                            fontSize: 13, transition: 'background 0.15s',
                                                        }}
                                                        onClick={() => {
                                                            // Find node in tree and select it
                                                            const findAndSelect = (nodes: ZoneNode[]): boolean => {
                                                                for (const n of nodes) {
                                                                    if (n.id === child.id) {
                                                                        handleSelectZone(n);
                                                                        return true;
                                                                    }
                                                                    if (n.children && findAndSelect(n.children)) return true;
                                                                }
                                                                return false;
                                                            };
                                                            findAndSelect(arborescence);
                                                        }}
                                                        onMouseEnter={e => (e.currentTarget.style.background = 'var(--divider)')}
                                                        onMouseLeave={e => (e.currentTarget.style.background = 'var(--bg)')}
                                                    >
                                                        <span style={{
                                                            width: 8, height: 8, borderRadius: '50%',
                                                            background: ZONE_COLORS[child.type] || '#94A3B8',
                                                            flexShrink: 0,
                                                        }} />
                                                        <span style={{ flex: 1, fontWeight: 500 }}>{child.nom}</span>
                                                        <span style={{ fontSize: 10, color: 'var(--text-muted)' }}>
                                                            {ZONE_TYPE_LABELS[child.type] || child.type}
                                                        </span>
                                                    </div>
                                                ))}
                                            </div>
                                        </div>
                                    )}

                                    {/* Actions */}
                                    <div style={{ display: 'flex', gap: 8 }}>
                                        <button
                                            className="btn btn-sm btn-primary"
                                            onClick={handleZoomToZone}
                                            style={{ flex: 1 }}
                                        >
                                            <FiTarget /> Centrer
                                        </button>
                                        <button
                                            className="btn btn-sm btn-secondary"
                                            onClick={() => {
                                                setLayer('clients');
                                            }}
                                            style={{ flex: 1 }}
                                        >
                                            <FiMapPin /> Voir clients
                                        </button>
                                    </div>
                                </>
                            ) : (
                                <div style={{ textAlign: 'center', padding: 32, color: 'var(--text-muted)', fontSize: 13 }}>
                                    Selectionnez une zone dans l'arborescence
                                </div>
                            )}
                        </div>
                    </div>
                )}
            </div>

            {/* Inline styles for tree items and animations */}
            <style>{`
                @keyframes spin {
                    to { transform: rotate(360deg); }
                }
                .zone-tree-item {
                    display: flex;
                    align-items: center;
                    gap: 6px;
                    padding: 6px 12px;
                    cursor: pointer;
                    transition: background 0.15s;
                    user-select: none;
                    font-size: 13px;
                }
                .zone-tree-item:hover {
                    background: var(--bg);
                }
                .zone-tree-item-active {
                    background: var(--primary-light) !important;
                    border-right: 3px solid var(--primary);
                }
                .zone-tree-chevron {
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    width: 18px;
                    height: 18px;
                    border-radius: 4px;
                    flex-shrink: 0;
                    color: var(--text-muted);
                    cursor: pointer;
                    transition: background 0.15s, color 0.15s;
                }
                .zone-tree-chevron:hover {
                    background: var(--divider);
                    color: var(--text);
                }
                .zone-tree-dot {
                    width: 8px;
                    height: 8px;
                    border-radius: 50%;
                    flex-shrink: 0;
                }
                .zone-tree-info {
                    display: flex;
                    flex-direction: column;
                    gap: 2px;
                    min-width: 0;
                    flex: 1;
                }
                .zone-tree-name {
                    font-weight: 600;
                    white-space: nowrap;
                    overflow: hidden;
                    text-overflow: ellipsis;
                    color: var(--text);
                }
                .zone-tree-item-active .zone-tree-name {
                    color: var(--primary-dark);
                }
                .zone-tree-meta {
                    display: flex;
                    align-items: center;
                    gap: 6px;
                }
                .zone-label-icon {
                    background: transparent !important;
                    border: none !important;
                }
                .leaflet-popup-content-wrapper {
                    border-radius: 10px !important;
                    box-shadow: 0 4px 16px rgba(0,0,0,0.12) !important;
                }
                .leaflet-popup-content {
                    font-family: 'DM Sans', sans-serif !important;
                    font-size: 13px !important;
                    line-height: 1.5 !important;
                    margin: 10px 14px !important;
                }
            `}</style>
        </div>
    );
};

export default Cartographie;
